import { TextInput, Loader, Flex, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState, useRef } from 'react';
import {
  ManifestModelSearchDropdown,
  ManifestModelSearchDropdownScenario,
} from './ManifestModelSearchDropdown';
import Icon from '../Icon';
import { getTrendingModels, quickSearch } from '../../api/models/models.api';
import styles from './ManifestModelSearch.module.scss';
import { useTranslation } from 'react-i18next';
import {
  GetAiModelQuickSearchResponseData,
  GetAiModelTrendingResponseData,
  PostAiModelAnalysisRequestBody,
} from '@manifest-cyber/types/interface/contracts/model-analysis';
import { ManifestCheckbox } from '../ManifestCheckbox/ManifestCheckbox';

type ManifestModelSearchProps = {
  recordSearch: (
    target: PostAiModelAnalysisRequestBody & { includeArxivData: boolean },
  ) => void;
};

function scenarioFrom({
  query,
  data,
  isLoading,
  isError,
  isTrendingLoading,
  trendingData,
}: {
  query: string;
  data: GetAiModelQuickSearchResponseData | undefined;
  isLoading: boolean;
  isError: boolean;
  isTrendingLoading: boolean;
  trendingData: GetAiModelTrendingResponseData | undefined;
}): ManifestModelSearchDropdownScenario {
  switch (true) {
    case query.trim() === '' &&
      !isTrendingLoading &&
      trendingData &&
      trendingData.length > 0:
      const trendingItems =
        trendingData
          .map((item) => ({
            kind: item.kind,
            owner: item.owner,
            repo: item.repo,
            revision: item.revision,
            type: item.type,
          }))
          .slice(0, 5) ?? [];
      return { kind: 'trending', items: trendingItems };
    case isLoading:
      return { kind: 'loading' };
    case isError:
      return { kind: 'error' };
    case data === undefined || data.length === 0:
      return { kind: 'no-results', query };
    default:
      const items = data.map((item) => ({
        kind: item.kind,
        owner: item.owner,
        repo: item.repo,
        revision: item.revision,
      }));
      return { kind: 'results', items };
  }
}

export function ManifestModelSearch(props: ManifestModelSearchProps) {
  const { recordSearch } = props;
  const { t } = useTranslation();
  const [query, setQuery] = useState<string>('');
  const [opened, setOpened] = useState<boolean>(false);
  const [includeArxivData, setIncludeArxivData] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['model-quick-search', query],
    queryFn: () => quickSearch({ query }),
    enabled: !!query,
    retry: false,
  });

  const { data: trendingData, isLoading: isTrendingLoading } = useQuery({
    queryKey: ['model-trending-search'],
    queryFn: () => getTrendingModels(),
    retry: false,
  });

  const scenario = scenarioFrom({
    query,
    data,
    isLoading: isLoading,
    isError,
    isTrendingLoading,
    trendingData,
  });

  const hasSomeQuery = query.trim() !== '';
  const hasSomeQueryAndIsLoading = hasSomeQuery && isLoading;

  const onSelect = (selectedTarget: PostAiModelAnalysisRequestBody) => {
    recordSearch({
      kind: selectedTarget.kind,
      repo: selectedTarget.repo,
      owner: selectedTarget.owner,
      revision: selectedTarget.revision,
      includeArxivData,
    });
    handleClear();
  };

  const handleClear = () => {
    setQuery('');
    setOpened(false);
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeArxivData(event.currentTarget.checked);
  };

  const loaderOrClearIcon = () => {
    if (hasSomeQueryAndIsLoading) {
      return <Loader size="xs" onClick={handleClear} />;
    }
    if (hasSomeQuery) {
      return (
        <div onClick={handleClear}>
          <Icon icon="xmark" />
        </div>
      );
    }
    return undefined;
  };

  return (
    <div className={styles.manifestModelSearchContainer}>
      <TextInput
        className={styles.manifestModelSearchInputContainer}
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpened(true)}
        icon={
          <div>
            <Icon icon="search" />
          </div>
        }
        rightSection={loaderOrClearIcon()}
        onBlur={() => setTimeout(() => setOpened(false), 150)}
        placeholder={t('page.ai-explore.searchPlaceholder')}
      />
      {opened && (
        <div className={styles.manifestModelSearchDropdownContainer}>
          <ManifestModelSearchDropdown scenario={scenario} onSelect={onSelect} />
        </div>
      )}

      <Flex align="center" justify="space-between" gap={5}>
        <ManifestCheckbox
          label={t('page.ai-explore.extractArXiData.label')}
          checked={includeArxivData}
          onChange={handleCheckboxChange}
        />

        <Tooltip label={t('page.ai-explore.extractArXiData.tooltip')}>
          <span>
            <Icon icon="question-circle" />
          </span>
        </Tooltip>
      </Flex>
    </div>
  );
}
